import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  useCliente,
  useAssumirAtendimento,
  useExcluirCliente,
  useArquivarCliente,
  useDesarquivarCliente,
  useTransferirAtendimento
} from '@/hooks/useClientes';

import { useEventos } from '@/hooks/useEventos';
import { useAtendentes } from '@/hooks/useProfiles';
import { useAuthContext } from '@/contexts/AuthContext';
import { ClienteEditForm } from '@/components/cliente/ClienteEditForm';
import { OrdemServicoFormDialog } from '@/components/ordens/OrdemServicoFormDialog';
import { HistoricoServicosCliente } from '@/components/clientes/HistoricoServicosCliente';
import { ProfileMedia } from '@/components/painel/ProfileMedia';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Phone, User, Clock, Loader2, Mail, MapPin, UserPlus, Trash2, Archive, ArchiveRestore, ArrowRightLeft } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ClientePotential() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, isAdmin } = useAuthContext();

  const { data: cliente, isLoading } = useCliente(id);
  const { data: eventos } = useEventos(id);
  const { data: atendentes } = useAtendentes();

  const assumirAtendimento = useAssumirAtendimento();
  const excluirCliente = useExcluirCliente();
  const arquivarCliente = useArquivarCliente();
  const desarquivarCliente = useDesarquivarCliente();
  const transferirAtendimento = useTransferirAtendimento();

  const [dialogTransferirAberto, setDialogTransferirAberto] = useState(false);
  const [novoResponsavelId, setNovoResponsavelId] = useState('');
  const [dialogCriarOS, setDialogCriarOS] = useState(false);

  const podeAssumir = !cliente?.responsavel_id && profile?.id;
  const podeGerenciar = profile?.id && (cliente?.responsavel_id === profile.id || isAdmin);
  const podeTransferir = isAdmin && cliente?.responsavel_id;

  const handleExcluirCliente = () => {
    if (!cliente?.id || !profile?.id) return;
    excluirCliente.mutate({
      clienteId: cliente.id,
      clienteNome: cliente.nome || undefined,
      profileId: profile.id,
      profileNome: profile.nome,
    });
  };

  const handleArquivarCliente = () => {
    if (!cliente?.id || !profile?.id) return;
    arquivarCliente.mutate({
      clienteId: cliente.id,
      clienteNome: cliente.nome || undefined,
      profileId: profile.id,
      profileNome: profile.nome,
    });
  };

  const handleDesarquivarCliente = () => {
    if (!cliente?.id || !profile?.id) return;
    desarquivarCliente.mutate({
      clienteId: cliente.id,
      clienteNome: cliente.nome || undefined,
      novoStatus: 'voltar_contato',
      profileId: profile.id,
      profileNome: profile.nome,
    });
  };

  const handleTransferirAtendimento = () => {
    if (!cliente?.id || !profile?.id || !novoResponsavelId) return;
    const novoAtendente = atendentes?.find(a => a.id === novoResponsavelId);
    if (!novoAtendente) return;
    transferirAtendimento.mutate({
      clienteId: cliente.id,
      novoResponsavelId: novoResponsavelId,
      novoResponsavelNome: novoAtendente.nome,
      responsavelAnteriorId: cliente.responsavel_id,
      responsavelAnteriorNome: cliente.responsavel?.nome || null,
      profileId: profile.id,
      profileNome: profile.nome,
    }, {
      onSuccess: () => {
        setDialogTransferirAberto(false);
        setNovoResponsavelId('');
      },
    });
  };

  const handleAssumirAtendimento = () => {
    if (!id || !profile?.id || !profile?.nome) return;
    assumirAtendimento.mutate({
      clienteId: id,
      profileId: profile.id,
      profileNome: profile.nome,
    });
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!cliente) {
    return (
      <MainLayout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">{t('clientPotential.notFound')}</p>
          <button
            type="button"
            className="ow-btn ow-btn--ghost ow-btn--sm mt-3"
            onClick={() => navigate('/painel')}
          >
            {t('clientPotential.backToPanel')}
          </button>
        </div>
      </MainLayout>
    );
  }

  const formatarEndereco = () => {
    const partes = [];
    if (cliente.endereco_rua) {
      let linha = cliente.endereco_rua;
      if (cliente.endereco_numero) linha += `, ${cliente.endereco_numero}`;
      partes.push(linha);
    }
    if (cliente.endereco_complemento) partes.push(cliente.endereco_complemento);
    if (cliente.endereco_codigo_postal || cliente.endereco_cidade) {
      partes.push(`${cliente.endereco_codigo_postal || ''} ${cliente.endereco_cidade || ''}`.trim());
    }
    if (cliente.endereco_regiao) partes.push(cliente.endereco_regiao);
    if (cliente.endereco_pais) partes.push(cliente.endereco_pais);
    return partes.length > 0 ? partes.join(', ') : null;
  };

  const enderecoFormatado = formatarEndereco();
  const atendentesDisponiveis = atendentes?.filter(a => a.id !== cliente.responsavel_id) || [];

  const clienteParaOS = {
    id: cliente.id,
    nome: cliente.nome,
    telefone: cliente.telefone,
    endereco_rua: cliente.endereco_rua,
    endereco_cidade: cliente.endereco_cidade,
    endereco_codigo_postal: cliente.endereco_codigo_postal,
    endereco_pais: cliente.endereco_pais,
    endereco_complemento: cliente.endereco_complemento,
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <button
          type="button"
          className="ow-btn ow-btn--ghost ow-btn--sm mb-4"
          onClick={() => navigate(`/painel?cliente=${id}`)}
        >
          <ArrowLeft size={14} /> {t('common.back')}
        </button>

        {/* Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex flex-col items-start gap-1">
                  {cliente.codigo_cliente && (
                    <Badge variant="outline" className="font-mono text-sm">
                      {cliente.codigo_cliente}
                    </Badge>
                  )}
                  <h1 className="text-2xl font-bold">{cliente.nome || t('clientPotential.noName')}</h1>
                </div>
                <div className="flex flex-col gap-1 text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {cliente.responsavel?.nome || t('clientPotential.notAssigned')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {cliente.telefone}
                  </span>
                  {cliente.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {cliente.email}
                    </span>
                  )}
                </div>
                {enderecoFormatado && (
                  <p className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {enderecoFormatado}
                  </p>
                )}
                {cliente.arquivado && (
                  <Badge variant="secondary" className="bg-muted">
                    <Archive className="h-3 w-3 mr-1" /> {t('clientPotential.archived')}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border">
                {podeAssumir && (
                  <button
                    type="button"
                    className="ow-btn ow-btn--primary"
                    onClick={handleAssumirAtendimento}
                    disabled={assumirAtendimento.isPending}
                  >
                    {assumirAtendimento.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                    {t('clientPotential.takeOver')}
                  </button>
                )}

                {podeGerenciar && !cliente.arquivado && (
                  <button
                    type="button"
                    className="ow-btn ow-btn--primary"
                    onClick={() => setDialogCriarOS(true)}
                    title={t('clientPotential.createOS')}
                  >
                    {t('clientPotential.createOS')}
                  </button>
                )}

                {podeTransferir && (
                  <Dialog open={dialogTransferirAberto} onOpenChange={setDialogTransferirAberto}>
                    <DialogTrigger asChild>
                      <button type="button" className="ow-btn ow-btn--outline ow-btn--icon ow-btn--sm" title={t('clientPotential.transfer')} aria-label={t('clientPotential.transfer')}>
                        <ArrowRightLeft className="h-4 w-4" />
                      </button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('clientPotential.transfer')}</DialogTitle>
                        <DialogDescription>{t('clientPotential.transferTitle')}</DialogDescription>
                      </DialogHeader>
                      <div className="py-4 space-y-4">
                        <p className="text-sm text-muted-foreground">
                          {t('clientPotential.currentResponsible', { name: cliente.responsavel?.nome || t('clientPotential.notAssigned') })}
                        </p>
                        <div className="space-y-2">
                          <Label>{t('clientPotential.newResponsible')}</Label>
                          <Select value={novoResponsavelId} onValueChange={setNovoResponsavelId}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('clientPotential.selectNewResponsible')} />
                            </SelectTrigger>
                            <SelectContent>
                              {atendentesDisponiveis.map(atendente => (
                                <SelectItem key={atendente.id} value={atendente.id}>
                                  {atendente.nome} {atendente.role === 'admin' ? '(Admin)' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <button type="button" className="ow-btn ow-btn--outline" onClick={() => setDialogTransferirAberto(false)}>
                          {t('common.cancel')}
                        </button>
                        <button
                          type="button"
                          className="ow-btn ow-btn--primary"
                          onClick={handleTransferirAtendimento}
                          disabled={!novoResponsavelId || transferirAtendimento.isPending}
                        >
                          {transferirAtendimento.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                          {t('clientPotential.confirmTransfer')}
                        </button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {podeGerenciar && (
                  <>
                    {!cliente.arquivado ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button type="button" className="ow-btn ow-btn--outline ow-btn--icon ow-btn--sm" title={t('clientPotential.archive')} aria-label={t('clientPotential.archive')}>
                            <Archive className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('clientPotential.archiveCardTitle')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('clientPotential.archiveCardDesc', { name: cliente.nome || t('clientPotential.noName') })}
                              <span className="block mt-2 text-sm font-medium text-foreground">
                                {t('clientPotential.archiveCardKeepsActive')}
                              </span>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={handleArquivarCliente} disabled={arquivarCliente.isPending}>
                              {arquivarCliente.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                              {t('clientPotential.confirmArchiveCard')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button type="button" className="ow-btn ow-btn--outline ow-btn--icon ow-btn--sm" title={t('clientPotential.unarchive')} aria-label={t('clientPotential.unarchive')}>
                            <ArchiveRestore className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('clientPotential.reactivateCardTitle')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('clientPotential.reactivateCardDesc', { name: cliente.nome || t('clientPotential.noName') })}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDesarquivarCliente} disabled={desarquivarCliente.isPending}>
                              {desarquivarCliente.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                              {t('clientPotential.confirmReactivateCard')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </>
                )}

                {isAdmin && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button type="button" className="ow-btn ow-btn--icon ow-btn--sm text-destructive !border-destructive hover:bg-destructive hover:text-destructive-foreground" title={t('clientPotential.deleteClient')} aria-label={t('clientPotential.deleteClient')}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('clientPotential.deletePermanentTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('clientPotential.deletePermanentDesc', { name: cliente.nome || t('clientPotential.noName') })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleExcluirCliente}
                          disabled={excluirCliente.isPending}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {excluirCliente.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          {t('clientPotential.confirmDeletePermanent')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="servicos">
          <TabsList className="mb-4">
            <TabsTrigger value="servicos">{t('clientPotential.services')}</TabsTrigger>
            <TabsTrigger value="historico">{t('clientPotential.history')}</TabsTrigger>
            <TabsTrigger value="dados">{t('clientPotential.data')}</TabsTrigger>
            <TabsTrigger value="midia">{t('clientPotential.media')}</TabsTrigger>
          </TabsList>

          <TabsContent value="servicos">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('clientPotential.serviceOrders')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <HistoricoServicosCliente clienteId={cliente.id} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="historico">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-6">
                  {eventos?.map(evento => (
                    <div key={evento.id} className="flex gap-4 items-start">
                      <div className="w-2 h-2 mt-2 rounded-full bg-primary shrink-0" />
                      <div className="space-y-1">
                        <p className="font-medium leading-snug">{evento.descricao}</p>
                        {evento.usuario && <p className="text-sm text-muted-foreground">{t('clientPotential.by', { name: evento.usuario.nome })}</p>}
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Clock className="h-3 w-3" />
                          {format(new Date(evento.criado_em), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!eventos || eventos.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">{t('clientPotential.noEvents')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dados">
            <ClienteEditForm cliente={cliente} />
          </TabsContent>

          <TabsContent value="midia">
            <Card>
              <CardContent className="p-4">
                <ProfileMedia clienteId={cliente.id} wide />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog de Criar OS */}
        <OrdemServicoFormDialog
          open={dialogCriarOS}
          onOpenChange={setDialogCriarOS}
          clientePreSelecionado={clienteParaOS}
        />
      </div>
    </MainLayout>
  );
}
